import CardExpansion from '@ifca-root/react-component/src/components/CardList/CardExpansion'
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core'
import { AccountFooter } from 'components/Footer/AccountFooter'
import { RejectDialog } from 'components/Dialog/RejectDialog'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  AcctPermission,
  ApprovalStatus,
  useActionOnAssetRevaluationStatusMutation,
} from 'generated/graphql'
import { usePermissionChecker } from 'helpers/Hooks/usePermissionChecker'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { formatDate } from 'helpers/StringNumberFunction/FormatDate'
import { amtNumStr, amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { RevaluationDetailContent } from '../RevaluationDetail/RevaluationDetailContent'

interface RevaluationPostingListProps {
  filteredList: any[]
  listLoading: boolean
  listStatus: string
  userList: any[]
  fetchMore: Function
  setOriginalListing: Function
  postIDs: Set<string>
  setPostIDs: Function
  CompanyID: string
}

export const RevaluationPostingList: React.FC<RevaluationPostingListProps> = ({
  filteredList,
  listLoading,
  listStatus,
  userList,
  fetchMore,
  setOriginalListing,
  postIDs,
  setPostIDs,
  CompanyID,
}) => {
  const { handlePermDisabled } = usePermissionChecker()
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)
  const [selectAll, setSelectAll] = useState(false)
  const getSearch = JSON.parse(localStorage.getItem('searchFilter'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */

  const [changeStatus, setChangeStatus] = useState('')
  const [openRejectDialog, setOpenRejectDialog] = useState(false)
  const [clickReject, setIsReject] = useState(false)
  const [rejectDialogInput, setRejectDialogInput] = useState({
    RejectionRemark: '',
  })
  const [errMessage, setErrMessage] = useState<string | null>(null)
  const [errDialog, setErrDialog] = useState(false)
  const [errApproveDialog, setErrApproveDialog] = useState(false)

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */

  const [
    approveRejectRevaluation,
    { loading: approveRejectLoading },
  ] = useActionOnAssetRevaluationStatusMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMessage(error)
      setErrApproveDialog(true)
      setPostIDs(new Set([]))
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      if (changeStatus === ApprovalStatus.Rejected) {
        setSnackBarMsg(SystemMsgs.rejectNewRecord())
      } else {
        setSnackBarMsg(SystemMsgs.createNewRecord())
      }
      setOriginalListing([])
      setPostIDs(new Set([]))
      fetchMore({
        variables: {
          CompanyID: CompanyID,
          StatusArr: ['SUBMIT'],
          take: 30,
          skip: 0,
          searchValue:
            getSearch?.name === '' || getSearch?.name === undefined
              ? undefined
              : getSearch?.name,
        },
      })
    },
  })

  const sortedList = useMemo(() => {
    return filteredList?.sort(
      (a, b) =>
        Object.values(ApprovalStatus).indexOf(a?.ApprovalStatus) -
        Object.values(ApprovalStatus).indexOf(b?.ApprovalStatus)
    )
  }, [filteredList])

  /* -------------------------------------------- */
  /*              CHECKBOX HANDLERS               */
  /* -------------------------------------------- */

  const handleCheckBoxChange = (
    e: ChangeEvent<HTMLInputElement>,
    revaluationID: string
  ) => {
    const temp = new Set(postIDs)

    if (e.target.checked) {
      temp.add(revaluationID)
    } else {
      temp.delete(revaluationID)
      setSelectAll(false)
    }

    setPostIDs(temp)
  }

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (listLoading) return

    const isChecked = e.target.checked
    setSelectAll(isChecked)

    const temp = new Set(
      isChecked ? checkedFilteredList.map(el => el.revaluation_id) : []
    )

    setPostIDs(temp)
  }

  const checkedFilteredList =
    filteredList && filteredList.length > 0 ? filteredList : []

  /* -------------------------------------------- */
  /*                    HELPERS                   */
  /* -------------------------------------------- */

  const getRevaluationIndicator = (el: any) => {
    const surplus = parseFloat(el?.revaluation_surplus || 0)
    const deficit = parseFloat(el?.revaluation_deficit || 0)

    if (surplus > 0) {
      return (
        <span className="desc" style={{ color: 'green' }}>
          Surplus: {amtStr(surplus)}
        </span>
      )
    } else if (deficit > 0) {
      return (
        <span className="desc" style={{ color: 'red' }}>
          Deficit: {amtStr(deficit)}
        </span>
      )
    }
    return (
      <span className="desc" style={{ color: '#666' }}>
        No Change
      </span>
    )
  }

  /* -------------------------------------------- */
  /*              VALIDATION & SUBMIT             */
  /* -------------------------------------------- */

  const validateSelection = (listRevaluationIDs: string[]) => {
    const validIDs = listRevaluationIDs.filter(id =>
      filteredList.some(item => item?.revaluation_id === id)
    )

    const invalidItems = validIDs.filter(id => {
      const item = filteredList.find(item => item?.revaluation_id === id)
      return item?.ApprovalStatus !== 'SUBMIT'
    })

    if (validIDs.length === 0) {
      setErrMessage('No valid items selected. Please refresh the page.')
      setErrDialog(true)
      return []
    }

    if (invalidItems.length > 0) {
      setErrMessage(
        'Some selected items are no longer available for approval. Please refresh the page.'
      )
      setErrDialog(true)
      return []
    }

    if (validIDs.length < listRevaluationIDs.length) {
      setErrMessage(
        `${listRevaluationIDs.length -
          validIDs.length} item(s) are no longer available. Proceeding with ${
          validIDs.length
        } valid item(s).`
      )
      setErrDialog(true)
    }

    return validIDs
  }

  const onSubmit = (listRevaluationIDs: string[], status: ApprovalStatus) => {
    const validIDs = validateSelection(listRevaluationIDs)

    if (validIDs.length === 0) {
      setPostIDs(new Set([]))
      return
    }

    if (validIDs.length < listRevaluationIDs.length) {
      setPostIDs(new Set(validIDs))
      return
    }

    setChangeStatus(status)
    approveRejectRevaluation({
      variables: {
        revaluation_ids: validIDs,
        ApprovalStatus: status,
        CompanyID: CompanyID,
        input:
          status === ApprovalStatus.Rejected
            ? {
                RejectionDate: new Date().toISOString(),
                RejectionRemark: rejectDialogInput?.RejectionRemark,
              }
            : undefined,
      },
    })
  }

  useEffect(() => {
    if (clickReject) {
      const listRevaluationIDs: string[] = Array.from(postIDs || [])
      onSubmit(listRevaluationIDs, ApprovalStatus.Rejected)
      setIsReject(false)
    }
  }, [clickReject])

  const listRevaluationIDs: string[] = Array.from(postIDs || [])

  return (
    <>
      {listLoading && <Loading />}
      {approveRejectLoading && <Loading />}

      <ContentWrapper float overflow>
        {listStatus === 'SUBMIT' && checkedFilteredList?.length !== 0 ? (
          <span className="mdDesc flex-space" style={{ paddingLeft: '7px' }}>
            <Checkbox
              value=""
              color="primary"
              onChange={handleSelectAll}
              style={{ marginLeft: '-5px', marginTop: '-5px' }}
              checked={
                postIDs?.size > 0 && postIDs.size === checkedFilteredList.length
              }
              disabled={
                listLoading ||
                handlePermDisabled({
                  companyID: CompanyID,
                  permEnum: AcctPermission.FixedAssetsRevaluationApproveReject,
                })
              }
            />
            <span style={{ marginLeft: '9px' }}>
              Select All{listLoading ? ' (Loading...)' : ''}
            </span>
          </span>
        ) : null}

        <List className="core-list">
          {checkedFilteredList === undefined ||
          checkedFilteredList?.length === 0 ? (
            <EmptyList
              title="No Record found"
              subtitle="Add a new record now."
            />
          ) : (
            <InfiniteScroll
              dataLength={checkedFilteredList?.length || 0}
              next={() => {
                const currentLength = filteredList?.length
                fetchMore({
                  variables: {
                    CompanyID: CompanyID,
                    StatusArr: [listStatus],
                    take: 30,
                    skip: currentLength,
                    searchValue:
                      getSearch?.name === '' || getSearch?.name === undefined
                        ? undefined
                        : getSearch?.name,
                  },
                })
              }}
              hasMore={true}
              style={{ padding: '8px 6px' }}
              loader={
                listLoading && (
                  <div style={{ textAlign: 'center' }}>
                    <CircularProgress />
                  </div>
                )
              }
              endMessage={
                <p style={{ textAlign: 'center' }}>
                  <b>-</b>
                </p>
              }
            >
              {checkedFilteredList.map((el, index) => (
                <CardExpansion
                  key={index}
                  backPanelColor="#f5f5f5"
                  summary={
                    <ListItem style={{ paddingBottom: '25px' }}>
                      {listStatus === 'SUBMIT' && (
                        <FormControl
                          component="fieldset"
                          style={{ width: '16px', paddingRight: '16px' }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                value={el.revaluation_id}
                                onChange={e =>
                                  handleCheckBoxChange(e, el.revaluation_id)
                                }
                                onClick={e => e.stopPropagation()}
                                checked={postIDs.has(el.revaluation_id)}
                                color="primary"
                                style={{
                                  marginLeft: '10px',
                                  marginRight: '-5px',
                                }}
                                disabled={handlePermDisabled({
                                  companyID: CompanyID,
                                  permEnum:
                                    AcctPermission.FixedAssetsRevaluationApproveReject,
                                })}
                              />
                            }
                            label=""
                          />
                        </FormControl>
                      )}
                      <ListItemText
                        primary={
                          <>
                            <span className="desc date-width">
                              {`${formatDate(el?.revaluation_date)}` || 'N/A'}
                            </span>
                            <span className="xsTitle flex-space">
                              {el?.DocNo}
                            </span>
                            <span className={`xsTitle`}>{el?.Asset?.name}</span>
                          </>
                        }
                        secondary={
                          <>
                            <Grid container spacing={0}>
                              <Grid container className="table-content">
                                <Grid item xs={6}>
                                  <span className="desc flex-space">
                                    {el?.Asset?.Category?.name}
                                  </span>
                                </Grid>
                                <Grid
                                  item
                                  xs={6}
                                  style={{ textAlign: 'right' }}
                                >
                                  <span className="desc">
                                    <span style={{ color: '#666' }}>
                                      {amtStr(el?.net_amount_before)}
                                    </span>
                                    {' → '}
                                    <span style={{ color: 'orange' }}>
                                      {amtStr(el?.net_amount_after)}
                                    </span>
                                  </span>
                                </Grid>
                              </Grid>

                              <Grid container>
                                <Grid item xs={6}>
                                  <span className="desc">{el?.remark}</span>
                                </Grid>
                                <Grid
                                  item
                                  xs={6}
                                  style={{ textAlign: 'right' }}
                                >
                                  {getRevaluationIndicator(el)}
                                </Grid>
                              </Grid>
                            </Grid>
                          </>
                        }
                      />
                    </ListItem>
                  }
                >
                  <RevaluationDetailContent
                    listEl={el}
                    documentListing={el?.Attachment}
                    listStatus={'SUBMIT'}
                    userList={userList}
                    mode={'expansion'}
                  />
                </CardExpansion>
              ))}
            </InfiniteScroll>
          )}
        </List>
      </ContentWrapper>

      {listStatus === 'SUBMIT' && (
        <AccountFooter
          options={[
            {
              name: 'Reject',
              onClick: () => {
                setOpenRejectDialog(true)
              },
              color: 'secondary',
              props: {
                disabled:
                  postIDs.size === 0 ||
                  handlePermDisabled({
                    companyID: CompanyID,
                    permEnum:
                      AcctPermission.FixedAssetsRevaluationApproveReject,
                  }),
              },
            },
            {
              name: 'Approve',
              onClick: () => {
                onSubmit(listRevaluationIDs, ApprovalStatus.Completed)
              },
              color: 'primary',
              props: {
                disabled:
                  postIDs.size === 0 ||
                  handlePermDisabled({
                    companyID: CompanyID,
                    permEnum:
                      AcctPermission.FixedAssetsRevaluationApproveReject,
                  }),
              },
            },
          ]}
        />
      )}

      <RejectDialog
        data={filteredList}
        openRejectDialog={openRejectDialog}
        setOpenRejectDialog={setOpenRejectDialog}
        setRejectDialogInput={setRejectDialogInput}
        setIsReject={setIsReject}
      />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />

      <ErrorDialog
        errorDia={errApproveDialog}
        setErrorDia={setErrApproveDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Approval Error'}
        isBool
        onclick={() => {
          setErrApproveDialog(false)
          setOriginalListing([])
          setPostIDs(new Set([]))
          fetchMore({
            variables: {
              CompanyID: CompanyID,
              StatusArr: ['SUBMIT'],
              take: 30,
              skip: 0,
              searchValue:
                getSearch?.name === '' || getSearch?.name === undefined
                  ? undefined
                  : getSearch?.name,
            },
          })
        }}
      />
    </>
  )
}
